/**
 * English (en-US) translation.
 * Must implement EXACTLY the same shape as `pt.ts` (enforced by `Translations` type).
 */
import type { Translations } from '..';

const en: Translations = {
  meta: {
    htmlLang: 'en-US',
  },
  common: {
    loading: 'Loading...',
    next: 'Next',
    finish: 'Finish',
    continue: 'Continue',
    submit: 'Pay',
    done: 'Done',
    copy: 'COPY',
    download: 'Download PDF',
    requiredField: 'Required field!',
    invalidField: 'Invalid field!',
  },
  languageSwitcher: {
    label: 'Language',
    pt: 'Portuguese',
    en: 'English',
    es: 'Spanish',
  },
  errors: {
    pixExpired: 'Pix expired! Please start a new purchase.',
    purchaseFailed: 'Purchase failed',
    portabilityFailed: 'Number transfer request failed',
    portabilityUndefined: 'Number transfer request failed: undefined response',
    portabilityNotScheduled: 'We could not schedule your number transfer. Contact us on WhatsApp to complete it.',
    cardRegisterUnknown: 'Unknown error while registering the card',
    purchaseUnknown: 'Purchase request error',
    invalidCoupon: 'Enter a valid coupon!',
    cepNotFound: 'Postal code not found. Please check the digits.',
    generic: 'We could not process your request. Please try again.',
    noConnection: 'Connection failed. Check your internet and try again.',
    byStatus: {
      400: 'Invalid request.',
      401: 'Session is not authenticated. Please reload the page.',
      402: 'Payment declined. Check your card details or try another card.',
      403: 'Access denied.',
      404: 'Resource not found.',
      405: 'Operation not allowed.',
      409: 'This operation is no longer available.',
      422: 'The information provided is invalid or incorrect.',
      429: 'Too many attempts. Please wait a moment.',
      500: 'Internal server error. Please try again.',
      502: 'Service temporarily unavailable. Please try again.',
      503: 'Service temporarily unavailable. Please try again.',
    },
    retryAfter: 'Please wait {{segundos}} seconds and try again.',
    paymentDeclined: 'Payment declined. Please check your card details.',
    paymentUnavailable: 'Payment is temporarily unavailable. Please try again later.',
    paymentAttemptsOver: 'Session ended after too many payment attempts.',
    purchaseExpired: 'This purchase expired or was not validated. Please start over.',
    summaryUnavailable: 'We could not load the purchase summary.',
    planUnavailable: 'The selected plan is no longer available. Please choose another plan to continue.',
    threeDSTimeout: "Your bank's authentication did not respond. Please try again — your purchase details are still valid.",
    threeDSUnavailable: "We couldn't load the payment security check. Reload the page; if the problem persists, contact us at ti@conteltelecom.com.br.",
    threeDSDeclined: '3DS authentication was declined by your bank. Try another card.',
    threeDSTechnicalError: 'Technical error in 3DS authentication. Please try again.',
    threeDSStartFailed: "Couldn't start 3DS authentication. Reload the page.",
    threeDSUnenrolled: "We couldn't authenticate this card with your bank. Check the details or use another card.",
    threeDSUnsupportedBrand: "This card's brand isn't supported for authentication. Use a card from another brand.",
    recaptchaFailed: 'reCAPTCHA validation failed. Please try again!',
  },
  snackbar: {
    pixCopied: 'Pix copied!',
    codeCopied: 'Code copied!',
  },
  page404: {
    notFound: 'Page not found',
  },
  page1: {
    sloganDefault: 'Supercharge your phone with top speed',
    uncomplicateDefault: 'Make it simple',
    detailsDefault1: 'Join more than 123k users',
    detailsDefault2: 'We have the right plan for you',
    bestSellersDefault1: 'Be like the best',
    bestSellersDefault2: 'Pick a plan that looks like you',
    chooseProfile1: 'Pick your profile',
    chooseProfile2: 'Tailored for you',
    perfilLabel: 'PROFILE',
    choosePlanTitle: 'Choose your plan',
    choosePlanSubtitle: 'You decide how much internet you need',
    chipAvulsoFallback: 'standalone',
    chipAvulsoDescription: 'Buy the SIM now and activate your plan later',
    bnftsTitle: 'SIM Benefits',
    whatsappFreeHtml: 'Free<br/> Whatsapp',
    callsInfiniteHtml: 'Unlimited<br/> Calls',
    wazeUnlimitedHtml: 'Unlimited<br/> Waze',
    activatedBnfts: {
      title: 'Activated Benefits',
      portabilityActivated: 'Number Transfer Activated',
      moreInternet: 'More internet for the same price',
      bonusGB: '+5GB Free',
    },
    planDetails: {
      whatsapp: 'WhatsApp included',
      callsUnlimited: 'Unlimited calls',
      callsLimited: '60min of calls',
      net5G: '5G Network',
      internetAccumulate: 'Internet that rolls over',
      noCutsSuffix: 'no throttling',
      nationalCoverage: 'Nationwide coverage',
      wazeUnlimited: 'Unlimited Waze',
      mobileIncluded: 'Included in your plan',
      mobileTechnology: 'Technology',
      mobileAccumulate: 'Roll over',
      mobileInternetAccumulation: 'Internet rollover',
      mobileNoCuts: 'No throttling',
      mobileInternet: 'Internet',
      mobileBonus: 'Bonus',
      mobileMonth: 'Every month',
    },
    selectPlanDetails: {
      portabilityOn: 'Bring my number from another carrier',
      portabilityOff: 'Bring my number from another carrier',
      gainingInternet: 'You are gaining more internet',
      losingInternet: 'You are losing internet',
      losingBadge: 'LOSE 5GB',
      gainingBadge: 'GAIN 5GB',
      mostPopular: 'MOST POPULAR',
      renewMonthly: 'Renew every month, no commitment',
      mostProfitable: 'BEST VALUE',
      buyTwelveForEight: 'Pay for 8 plans and get 12',
      chipPhysical: 'Physical SIM',
      becomeClient: 'To become a client',
      becomeClientFallback: 'To become our client',
      monthly: 'Monthly',
      annual: 'Yearly',
    },
    invite: {
      inviteFrom: 'You received an invite from',
      toBePartOf: 'to join',
      configureYourPlan: 'SET UP YOUR PLAN',
    },
    voucher: {
      iHaveCode: 'I have a code',
      brandClub: '{{brand}} Club',
      digitCode: 'Enter the referral code',
      placeholder: '#FE2025',
      validate: 'VALIDATE',
      invalidCode: 'Enter a valid voucher!',
      validCode: 'Valid voucher!',
    },
    timer: {
      limitedTime: 'Limited Time',
      gainBonus: 'Earn 5 GB by bringing your number',
    },
    slider: {
      internetAmount: 'Internet Amount',
    },
  },
  page2: {
    headingDefaultTitles: {
      step0: 'Set Up Your Plan',
      step0Sub: 'Pick more options and tailor your plan',
      formStep: 'Personal Information',
      formStepSub: 'Fill in the required fields',
      paymentStep: 'Choose how you want to pay',
      paymentStepSub: 'Pick more options and tailor your plan',
      step4: 'Payment',
      step4Sub: 'Complete the payment for your purchase',
    },
    stepper: {
      yourPlan: 'Your Plan',
      info: 'Information',
      address: 'Address',
      paymentMethod: 'Payment method',
      payment: 'Payment',
    },
    chipInfo: {
      physicalChipHtml: 'Physical SIM <span>(One-time Purchase)</span>',
      physicalChipDesc: 'A SIM card you insert into your device',
      receiveAtHome: 'Receive at home',
      receiveAtHomeDesc: 'Get the SIM at your address',
      pickUpInStore: 'Pick up at store',
      pickUpInStoreDesc: 'Pick up the SIM at the nearest store',
      virtualChip: 'Virtual SIM',
      virtualChipDesc: 'SIM for modern phones (e.g. iPhone 12)',
    },
    pickUpChip: {
      atHome: 'Receive at home',
      atHomeDesc: 'Get the SIM at your address',
      atStore: 'Pick up at store',
      atStoreDesc: 'See on map',
    },
    sectionTitles: {
      chipReceive: 'Receiving the physical SIM',
      chipReceiveSub: 'Choose how the SIM gets to you',
      accessTechnology: 'Access technology',
      accessTechnologySub: 'How you will connect',
      bonusGain: 'Earn a Bonus',
      bonusGainSub: 'See how to boost your internet',
    },
    smartControl: {
      title: 'Smart Contract',
      subtitle: 'Same price with more internet',
      bonusBadge: '+5GB FREE',
    },
    personalInfo: {
      title: 'Personal Information',
      subtitle: 'How can we identify you',
      fullName: 'Full Name',
      cpfCnpj: 'Tax ID',
      birthdate: 'Date of Birth',
    },
    contactInfo: {
      title: 'Contact Information',
      subtitle: 'How can we reach you',
      email: 'Email',
      ddd: 'Area code',
      phone: 'Phone',
    },
    addressInfo: {
      title: 'Address Information',
      subtitle: 'Where we can reach you',
      cep: 'ZIP code',
      state: 'State',
      city: 'City',
      street: 'Street',
      neighborhood: 'Neighborhood',
      number: 'Number',
      complement: 'Complement',
      reference: 'Landmark',
    },
    payment: {
      oneTimeTitle: 'Pay upfront',
      oneTimeSubtitle: 'Select one of the upfront payment options',
      installmentTitle: 'Pay in installments',
      installmentSubtitle: 'Pay in up to 12x on our payments site',
      finalizeInstallmentTitle: 'Finish your installment purchase',
      finalizeInstallmentSubtitle: 'Click "Pay" and you will be redirected to complete the payment.',
      cardTitle: 'Credit Card',
      cardSubtitle: 'We accept all cards',
      cardBadge: 'RECOMMENDED',
      cardNumber: 'Card Number',
      cardName: 'Full Name',
      cardCpf: 'Card Holder Tax ID',
      cardExpiry: 'Expiry Date',
      cardCvv: 'Security Code',
      keepRecurring: 'Keep auto-renew on the credit card',
      pixTitle: 'PIX',
      pixSubtitle: 'Pay with your banking app',
      pixBadge: 'INSTANT PAYMENT',
      pixStep1Title: 'Open your banking app',
      pixStep1Desc: 'You can pay with the bank of your choice.',
      pixStep2Title: 'Scan or type the PIX key',
      pixStep2Desc: 'Point your camera at the image or copy the code',
      pixStep3Title: 'Done! Wait for confirmation',
      pixStep3Desc: 'Once confirmed you will be redirected to the next page',
      pixExpiresIn: 'EXPIRES IN: ',
      pixTimeRemaining: 'Time remaining',
      boletoTitle: 'Bank Slip',
      boletoSubtitle: 'Up to 2 business days',
      boletoBadge: 'Pay with bank slip',
      boletoStep1Title: 'Copy the code and pay with your bank',
      boletoStep1Desc: 'You can pay with the bank of your choice.',
      wppFinalizeTitle: 'Finish the payment on WhatsApp!',
      wppFinalizeDesc: 'Click "Continue" and finish the order with one of our agents.',
      paymentApproved: 'Payment approved! Redirecting...',
      unixCardDescription: 'Use your Unix card to pay for your purchase',
      redoPurchase: 'Start over',
      paymentDeadlineExpired: 'The payment deadline has expired. Please start your purchase again.',
      payWithin: 'Complete the payment within {{tempo}}',
    },
    summary: {
      total: 'Total of this purchase',
      orderTitle: 'Your order summary',
      orderSubtitle: 'Details of your order',
      secondTitle: 'Your order summary',
      secondSubtitle: 'Everything included in your order with us',
      bonusTitle: 'Plan Bonus',
      bonusSubtitle: 'Get more internet on your plan',
      portability: 'Number Transfer',
      portabilityDesc: 'Switch carrier and keep your number',
      portabilityBonus: '+5GB FREE',
      smartControl: 'Smart Control',
      smartControlDesc: 'Monthly payment with 12-month commitment',
      chipPhysical: 'Physical SIM',
      chipVirtual: 'Virtual SIM',
      chipValue: 'SIM Cost',
      chipPaymentOnce: 'One-time Payment',
      chipDigitalDelivery: 'Digital Delivery',
      shipping: 'Shipping',
      shippingDesc: 'Ships immediately',
      paymentMonthly: 'Monthly payment',
      paymentOneTime: 'Upfront payment',
      chipUnit: 'SIM',
      chipUnitPlural: 'SIMs',
    },
    coupon: {
      title: 'Discounts',
      remove: 'Remove',
      active: 'Coupon active',
      codeLabel: 'Discount Code',
      activate: 'Apply',
      activateLong: 'Apply Coupon',
    },
  },
  page3: {
    welcomeGreeting1: 'Welcome to',
    welcomeGreeting2: 'Connect,',
    welcomeDescription: 'You are now part of the communication revolution! Enjoy your new plan.',
    nextStepsTitle: 'Next Steps',
    nextStepsSubtitle: 'Finish setting up your account here',
    plan: {
      monthlyTitle: 'Your Monthly Plan',
      annualTitle: 'Your Yearly Plan',
      monthlyDesc: 'See how much monthly internet you get',
      smartControlBadge: '+5GB CONTRACT',
      portabilityBadge: '+5GB TRANSFER',
    },
    stepper: {
      whatsappHelp: 'I need help',
      esim: 'eSIM',
      installEsim: 'eSIM installation',
      installEsimDesc: 'Open the PDF below to start the setup process',
      portabilityTitle: 'NUMBER TRANSFER',
      schedulePortability: 'Schedule Number Transfer',
      portabilityWarning: 'No worries — the process only starts once your Contel SIM is activated.',
      whichPhoneTransfer: 'Which phone number should be transferred to Contel?',
      whichOperator: 'Which carrier is this number currently on?',
      confirmEverything: 'Confirm everything is correct',
      brandFallback: 'Connect',
      leaving: 'Leaving',
      entering: 'Joining',
      attention: 'Attention',
      attentionText: 'By finishing I confirm this number is active and registered to my Tax ID',
      allDone: 'All set',
      portabilityProcessNote: 'Within 3 business days after the transfer request is confirmed, your number will be migrated.',
      esimInstructions: 'Click Finish to confirm your transfer request',
      chipInstructions: 'When your physical SIM arrives, activate it normally',
      followingSmsConfirm: 'Then reply YES to the confirmation SMS that will arrive shortly',
      buildYourPlan: 'Build your plan',
      buildYourPlanDone: 'Plan successfully completed',
      installEsimTitle: 'Install eSIM',
      installEsimComplete: 'Complete the SIM installation',
      scheduleTitle: 'Schedule Number Transfer',
      scheduleSubtitlePrefix: 'Bring your number to',
      scheduleSubtitleFallbackBrand: 'our carrier',
      smartControlTitle: 'Smart  Control',
      smartControlSubtitle: 'Set up your contract',
      doubts: 'Questions?',
      doubtsSubtitle: '24/7 support on WhatsApp',
    },
    smartControlModal: {
      title: 'SMART CONTROL',
      done: 'DONE',
    },
  },
  page4: {
    downloadApp: 'Download our app to stay up to date with everything!',
    slogan1: 'All set,',
    slogan2: 'you can',
    slogan3: 'enjoy your account',
    sloganVirtual1: 'You will receive an',
    sloganVirtual2: 'SMS',
    sloganVirtual3: 'to set up your plan',
  },
  footer: {
    titleDefault: '#KeepItSimple with',
    aboutPrefix: 'ABOUT',
    askDoubts: 'GET ANSWERS',
    lgpd: 'PRIVACY',
    contact: 'CONTACT US',
    copyrightPrefix: '© Group',
    copyrightFallbackBrand: 'Contel',
    copyrightSuffix: 'All rights reserved.',
  },
};

export default en;
